// V1 source: shared/hooks/useBreakpoint.ts (different thresholds)
// Strategy: scratch
// Verified no-MUI: yes
//
// MIDAS V2-specific breakpoint hook. Thresholds match the Paper
// `MIDAS V2 — Phase 2 — Dock — Spec` artboard production values:
//
//   phone   ≤768px
//   tablet  769–1200px
//   desktop ≥1201px
//
// These differ from `shared/hooks/useBreakpoint.ts` (768/1280) which the
// chat area already uses. The dock spec calls for desktop = 1201+ so a
// 1280px breakpoint would force the dock-collapse decision a step too late
// (a 1201px viewport should still get the side-by-side dock).
//
// Pure `window.matchMedia`. NO MUI dependency.
//
// Lives under `customer/midas/components/v2/hooks/` (not `shared/`) because
// it's V2-specific. New non-MIDAS pages should keep using the shared hook.

import { useEffect, useState } from "react";

const PHONE_QUERY = "(max-width: 768px)";
const TABLET_QUERY = "(min-width: 769px) and (max-width: 1200px)";
const DESKTOP_QUERY = "(min-width: 1201px)";

interface MidasBreakpoint {
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const computeFromWindow = (): MidasBreakpoint => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    // SSR / non-browser fallback — assume desktop (V2 is web-only anyway).
    return { isPhone: false, isTablet: false, isDesktop: true };
  }
  return {
    isPhone: window.matchMedia(PHONE_QUERY).matches,
    isTablet: window.matchMedia(TABLET_QUERY).matches,
    isDesktop: window.matchMedia(DESKTOP_QUERY).matches,
  };
};

export const useMidasBreakpoint = (): MidasBreakpoint => {
  const [bp, setBp] = useState<MidasBreakpoint>(computeFromWindow);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const phoneMql = window.matchMedia(PHONE_QUERY);
    const tabletMql = window.matchMedia(TABLET_QUERY);
    const desktopMql = window.matchMedia(DESKTOP_QUERY);

    const handler = () => setBp(computeFromWindow());

    // addEventListener is the modern API; addListener is the deprecated fallback
    // (still required for Safari < 14, which a small slice of factory-floor
    // tablets may run).
    if (typeof phoneMql.addEventListener === "function") {
      phoneMql.addEventListener("change", handler);
      tabletMql.addEventListener("change", handler);
      desktopMql.addEventListener("change", handler);
    } else if (typeof (phoneMql as any).addListener === "function") {
      (phoneMql as any).addListener(handler);
      (tabletMql as any).addListener(handler);
      (desktopMql as any).addListener(handler);
    }

    // Sync once on mount in case state was computed before the MQLs settled
    handler();

    return () => {
      if (typeof phoneMql.removeEventListener === "function") {
        phoneMql.removeEventListener("change", handler);
        tabletMql.removeEventListener("change", handler);
        desktopMql.removeEventListener("change", handler);
      } else if (typeof (phoneMql as any).removeListener === "function") {
        (phoneMql as any).removeListener(handler);
        (tabletMql as any).removeListener(handler);
        (desktopMql as any).removeListener(handler);
      }
    };
  }, []);

  return bp;
};

export default useMidasBreakpoint;
